import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, LogIn, Shield, Loader2, AlertCircle, Sparkles } from 'lucide-react';

export const DEVOARD_LOGO_URL = 'https://devoard.com/LOGO.png';

export const DEMO_CREDENTIALS = {
  email: 'demo@devoard.app',
  password: 'Demo@123',
};

function Wordmark({ size = 'md' }) {
  return (
    <div className="flex items-center justify-center gap-2 select-none">
      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-indigo-600 shadow-lg shadow-primary-500/30">
        <span className="text-white font-bold text-lg font-display">D</span>
      </div>
      <div className="text-left">
        <p className="font-display font-bold text-gray-900 leading-none text-lg">Devoard</p>
        <p className="text-[10px] font-semibold tracking-[0.25em] text-primary-600 uppercase leading-none mt-0.5">POS</p>
      </div>
    </div>
  );
}

/**
 * Devoard logo image with a graceful in-app wordmark fallback if the hosted
 * logo can't be loaded.
 */
export function DevoardLogo({ className = 'h-14 w-auto', alt = 'Devoard' }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <Wordmark />;
  return (
    <img
      src={DEVOARD_LOGO_URL}
      alt={alt}
      onError={() => setFailed(true)}
      className={`object-contain drop-shadow-md ${className}`}
    />
  );
}

function FloatingInput({
  id,
  label,
  type = 'text',
  icon: Icon,
  value,
  onChange,
  error,
  autoFocus,
  autoComplete,
  rightSlot,
}) {
  const [focused, setFocused] = useState(false);
  const floated = focused || String(value ?? '').length > 0;

  return (
    <div>
      <div
        className={`group relative rounded-xl border bg-white/70 backdrop-blur transition-all duration-200 ${
          error
            ? 'border-danger-300 focus-within:border-danger-400 focus-within:ring-4 focus-within:ring-danger-500/10'
            : 'border-gray-200 focus-within:border-primary-500 focus-within:ring-4 focus-within:ring-primary-500/10 hover:border-gray-300'
        }`}
      >
        {Icon && (
          <Icon
            size={17}
            className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
              floated ? 'text-primary-500' : 'text-gray-400'
            }`}
          />
        )}
        <input
          id={id}
          name={id}
          type={type}
          value={value}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          className="peer w-full rounded-xl bg-transparent px-11 pb-1.5 pt-5 text-sm text-gray-900 outline-none placeholder-transparent"
          placeholder={label}
        />
        <label
          htmlFor={id}
          className={`pointer-events-none absolute left-11 transition-all duration-200 ${
            floated
              ? 'top-2.5 text-[11px] font-semibold tracking-wide'
              : 'top-1/2 -translate-y-1/2 text-sm font-normal'
          } ${error ? 'text-danger-500' : floated ? 'text-primary-600' : 'text-gray-500'}`}
        >
          {label}
        </label>
        {rightSlot && <div className="absolute right-2 top-1/2 -translate-y-1/2">{rightSlot}</div>}
      </div>
      {error && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-danger-500">
          <AlertCircle size={12} className="flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

export default function LoginForm({
  onSubmit,
  loading = false,
  error,
  logo,
  shopName = 'Devoard POS',
  require2FA = false,
}) {
  const [form, setForm] = useState({ email: '', password: '', remember: false, token: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email address';
    if (!form.password) errs.password = 'Password is required';
    if (require2FA && !form.token.trim()) errs.token = '2FA code is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit?.(form);
  };

  const handleDemoLogin = () => {
    setForm((p) => ({
      ...p,
      email: DEMO_CREDENTIALS.email,
      password: DEMO_CREDENTIALS.password,
    }));
    setErrors({});
    onSubmit?.({
      email: DEMO_CREDENTIALS.email,
      password: DEMO_CREDENTIALS.password,
      remember: false,
      token: '',
    });
  };

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((v) => !v)}
      className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
    </button>
  );

  return (
    <div className="w-full">
      <div className="rounded-3xl border border-white/60 bg-white/75 p-8 shadow-2xl shadow-indigo-500/10 backdrop-blur-2xl sm:p-10">
        <div className="mb-8 text-center">
          <div className="flex justify-center">
            <DevoardLogo />
          </div>
          <h2 className="mt-6 font-display text-2xl font-bold text-gray-900">Welcome back</h2>
          <p className="mt-1 text-sm text-gray-500">Sign in to your {shopName} workspace</p>
        </div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              initial={{ opacity: 0, y: -8 }}
              animate={{
                opacity: 1,
                y: 0,
                x: [0, -8, 8, -5, 5, 0],
              }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ x: { duration: 0.45, ease: 'easeInOut' }, default: { duration: 0.25 } }}
              className="mb-6 flex items-center gap-2.5 rounded-xl border border-danger-200 bg-danger-50/80 px-4 py-3 text-sm text-danger-600"
            >
              <AlertCircle size={16} className="flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          <FloatingInput
            id="email"
            label="Email address"
            type="email"
            icon={Mail}
            value={form.email}
            error={errors.email}
            autoFocus
            autoComplete="email"
            onChange={(v) => setForm((p) => ({ ...p, email: v }))}
          />

          <FloatingInput
            id="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            icon={Lock}
            value={form.password}
            error={errors.password}
            autoComplete="current-password"
            onChange={(v) => setForm((p) => ({ ...p, password: v }))}
            rightSlot={passwordToggle}
          />

          {require2FA && (
            <FloatingInput
              id="2fa-token"
              label="2FA code"
              icon={Shield}
              value={form.token}
              error={errors.token}
              autoComplete="one-time-code"
              onChange={(v) => setForm((p) => ({ ...p, token: v }))}
            />
          )}

          <div className="flex items-center justify-between pt-1">
            <label className="flex cursor-pointer select-none items-center gap-2.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.remember}
                onChange={(e) => setForm((p) => ({ ...p, remember: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 accent-primary-600"
              />
              Remember me
            </label>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              Forgot password?
            </Link>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-primary-500/35 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/30 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <LogIn size={18} />
            )}
            {loading ? 'Signing in…' : 'Sign in'}
          </motion.button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white/80 px-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 backdrop-blur">
              Want a quick look?
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-primary-300 bg-primary-50/40 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100">
              <Sparkles size={16} className="text-primary-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-800">Try the demo account</p>
              <p className="mt-0.5 text-xs text-gray-500">
                Explore the POS with sample data — no sign-up needed.
              </p>
              <div className="mt-2.5 rounded-lg bg-white/80 px-3 py-2 font-mono text-[11px] text-gray-600 ring-1 ring-inset ring-gray-100">
                <p>
                  <span className="text-gray-400">Email:</span> {DEMO_CREDENTIALS.email}
                </p>
                <p>
                  <span className="text-gray-400">Password:</span> {DEMO_CREDENTIALS.password}
                </p>
              </div>
              <button
                type="button"
                onClick={handleDemoLogin}
                disabled={loading}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-primary-300 bg-white py-2.5 text-sm font-semibold text-primary-700 shadow-sm transition-all duration-200 hover:border-primary-400 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Sparkles size={15} />
                Login as Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
